import './option-groups-editor.scss';

import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';
import { ReactComponent as DragHandleIcon } from '../../../icons/drag-handle.svg';
import { ReactComponent as PlusIcon } from '../../../icons/plus.svg';
import { ReactComponent as WarningIcon } from '../../../icons/warning.svg';

import { Button } from '../button/Button';

const emptyOption = () => ({ id: null, label: '', priceDelta: 0 });

const emptyGroup = () => ({
  id: null,
  name: '',
  required: false,
  selectionType: 'SINGLE',
  options: [emptyOption()]
});

const hasInvalidOption = (group) => (group.options ?? []).some((option) => !option.label?.trim());

export const OptionGroupsEditor = ({ value, onChange }) => {
  const groups = value ?? [];

  const updateGroup = (index, changes) => {
    onChange(groups.map((group, i) => (i === index ? { ...group, ...changes } : group)));
  };

  const removeGroup = (index) => {
    onChange(groups.filter((_, i) => i !== index));
  };

  const addGroup = () => {
    onChange([...groups, emptyGroup()]);
  };

  const updateOption = (groupIndex, optionIndex, changes) => {
    const group = groups[groupIndex];
    const options = group.options.map((option, i) =>
      i === optionIndex ? { ...option, ...changes } : option
    );
    updateGroup(groupIndex, { options });
  };

  const removeOption = (groupIndex, optionIndex) => {
    const group = groups[groupIndex];
    updateGroup(groupIndex, { options: group.options.filter((_, i) => i !== optionIndex) });
  };

  const addOption = (groupIndex) => {
    const group = groups[groupIndex];
    updateGroup(groupIndex, { options: [...group.options, emptyOption()] });
  };

  return (
    <div className="option-groups-editor">
      <div className="option-groups-editor__header">
        <p className="option-groups-editor__subtitle">
          Groups of choices customers pick from when they add this item to their cart. Each option
          can adjust the item price.
        </p>
        <Button type="button" small className="option-groups-editor__add-group" onClick={addGroup}>
          <PlusIcon />
          Add option group
        </Button>
      </div>

      <div className="option-groups-editor__groups">
        {groups.map((group, groupIndex) => (
          <div className="option-groups-editor__group" key={group.id ?? `new-group-${groupIndex}`}>
            <div className="option-groups-editor__group-head">
              <div className="option-groups-editor__name-field">
                <input
                  type="text"
                  placeholder="Group name"
                  value={group.name}
                  onChange={(e) => updateGroup(groupIndex, { name: e.target.value })}
                />
              </div>
              <div className="option-groups-editor__type-field">
                <select
                  value={group.selectionType}
                  onChange={(e) => updateGroup(groupIndex, { selectionType: e.target.value })}
                >
                  <option value="SINGLE">Pick one</option>
                  <option value="MULTI">Pick any</option>
                </select>
                <ChevronDownIcon />
              </div>
              <label className="option-groups-editor__required">
                <span
                  className={`option-groups-editor__checkbox${group.required ? ' option-groups-editor__checkbox--checked' : ''}`}
                >
                  {group.required && <CheckIcon />}
                </span>
                <input
                  type="checkbox"
                  checked={group.required}
                  onChange={(e) => updateGroup(groupIndex, { required: e.target.checked })}
                />
                Required
              </label>
              <button
                type="button"
                className="option-groups-editor__icon-button"
                title="Remove group"
                onClick={() => removeGroup(groupIndex)}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="option-groups-editor__group-body">
              <div className="option-groups-editor__option-head-row">
                <span className="option-groups-editor__option-head">Option</span>
                <span className="option-groups-editor__price-head">Price change</span>
                <span className="option-groups-editor__spacer" />
              </div>

              <div className="option-groups-editor__option-list">
                {group.options.map((option, optionIndex) => {
                  const invalid = !option.label?.trim();
                  return (
                    <div className="option-groups-editor__option-row" key={option.id ?? `new-option-${optionIndex}`}>
                      <span className="option-groups-editor__drag-handle">
                        <DragHandleIcon />
                      </span>
                      <div
                        className={`option-groups-editor__label-field${invalid ? ' option-groups-editor__label-field--invalid' : ''}`}
                      >
                        <input
                          type="text"
                          placeholder="Option label"
                          value={option.label}
                          onChange={(e) => updateOption(groupIndex, optionIndex, { label: e.target.value })}
                        />
                      </div>
                      <div className="option-groups-editor__price-field">
                        <span>R</span>
                        <input
                          type="number"
                          step="0.01"
                          value={option.priceDelta ?? 0}
                          onChange={(e) =>
                            updateOption(groupIndex, optionIndex, { priceDelta: Number(e.target.value) || 0 })
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="option-groups-editor__icon-button"
                        title="Remove option"
                        onClick={() => removeOption(groupIndex, optionIndex)}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  );
                })}
              </div>

              {hasInvalidOption(group) && (
                <div className="option-groups-editor__error">
                  <WarningIcon />
                  Give every option a label before saving.
                </div>
              )}

              <button type="button" className="option-groups-editor__add-option" onClick={() => addOption(groupIndex)}>
                Add option
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
